import {
  Component,
  computed,
  input,
  Optional,
  Self,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldControl,
} from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MaterialModule } from '@fullyops/shared/material.module';
import {
  addExtraMinuteOption,
  minuteOptions,
} from '@fullyops/shared/util/time';

const granularity = 15;
const minutesPerDay = 1440;

@Component({
  selector: 'fo-time-input',
  standalone: true,
  templateUrl: './time-input.component.html',
  styleUrl: './time-input.component.scss',
  imports: [MaterialModule],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MatFormFieldControl,
      useExisting: TimeInputComponent,
    },
  ],
})
export class TimeInputComponent
  implements ControlValueAccessor, MatFormFieldControl<number | null>
{
  min = input(0);
  max = input(minutesPerDay);

  private defaultTimeOptions = computed(() =>
    minuteOptions(granularity, this.min(), this.max()),
  );
  protected timeOptions = computed(() => {
    const v = this.internalControlValue();
    const defaults = this.defaultTimeOptions();
    return v == null ||
      (v >= this.min() &&
        v <= this.max() &&
        (v - this.min()) % granularity === 0)
      ? defaults
      : addExtraMinuteOption(defaults, v);
  });

  protected internalControlValue = signal<number | null>(null);
  protected internalControlDisabled = signal(false);

  protected onInternalChange(value: number | null) {
    this.internalControlValue.set(value);
    this.onChange(value);
    this.onTouched();
  }

  // implementation of ControlValueAccessor to enable use with reactive forms

  private onChange = (v: number | null) => {};
  private onTouched = () => {};

  writeValue(v: number | null): void {
    this.internalControlValue.set(v);
  }

  registerOnChange(f: (v: number | null) => void) {
    this.onChange = f;
  }

  registerOnTouched(f: () => void): void {
    this.onTouched = f;
  }

  setDisabledState(isDisabled: boolean): void {
    this.internalControlDisabled.set(isDisabled);
  }

  // implementation of MatFormFieldControl to enable use inside mat-form-field

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
  }

  @ViewChild(MatSelect, { static: true })
  protected internalSelect: MatSelect;

  // These are not used when using this component with reactive forms, but they need to be
  // present regardless.
  get value(): number | null {
    return this.internalSelect.value;
  }
  set value(v: number | null) {
    this.writeValue(v);
  }

  get stateChanges() {
    return this.internalSelect.stateChanges;
  }

  get id() {
    return this.internalSelect.id;
  }

  get placeholder() {
    return this.internalSelect.placeholder;
  }

  get focused() {
    return this.internalSelect.focused;
  }

  get empty(): boolean {
    return this.internalSelect.empty;
  }

  get shouldLabelFloat(): boolean {
    return this.internalSelect.shouldLabelFloat;
  }

  get required() {
    return this.ngControl.control.hasValidator(Validators.required);
  }

  get disabled() {
    return this.ngControl.disabled;
  }

  get errorState() {
    return this.ngControl.touched && this.ngControl.errors !== null;
  }

  get controlType() {
    return this.internalSelect.controlType;
  }

  get autofilled() {
    return false;
  }

  get userAriaDescribedBy() {
    return this.internalSelect.userAriaDescribedBy;
  }

  setDescribedByIds(ids: string[]) {
    this.internalSelect.setDescribedByIds(ids);
  }

  onContainerClick(event: MouseEvent) {
    this.internalSelect.onContainerClick(event);
  }
}
